import React, { useState, useEffect, useMemo, useRef } from 'react';
import './FormSection.css';
import { QuestionnaireService } from '../lib/questionnaireService';

// Generate UUID v4
const generateUUIDv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

const NeuralPoints = React.memo(() => {
  const points = useMemo(() => {
    return Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 3,
      duration: 4 + Math.random() * 4
    }));
  }, []);

  return (
    <div className="neural-network" aria-hidden="true">
      {points.map((point) => (
        <div
          key={point.id}
          className="neural-point"
          style={{
            left: `${point.x}%`,
            top: `${point.y}%`,
            animationDelay: `${point.delay}s`,
            animationDuration: `${point.duration}s`
          }}
        />
      ))}
    </div>
  );
});

NeuralPoints.displayName = 'NeuralPoints';

const FormSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [displayedText, setDisplayedText] = useState('');
  const [showFields, setShowFields] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isWaitingForAI, setIsWaitingForAI] = useState(false);
  const [aiQuestion, setAiQuestion] = useState('');
  const [hasAIStep, setHasAIStep] = useState(false);
  const [isFormCompleted, setIsFormCompleted] = useState(false);
  const [phoneArea, setPhoneArea] = useState('');
  const [phoneFirst, setPhoneFirst] = useState('');
  const [phoneLast, setPhoneLast] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Refs for accessibility
  const modalRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const nextButtonRef = useRef(null);
  const backButtonRef = useRef(null);

  // Session ID management - generate and persist in localStorage
  useEffect(() => {
    const getOrCreateSessionId = () => {
      try {
        // Check if session_id already exists in localStorage
        let existingSessionId = localStorage.getItem('nukard_session');
        
        if (!existingSessionId) {
          // Generate new UUID v4 session_id
          existingSessionId = generateUUIDv4();
          localStorage.setItem('nukard_session', existingSessionId);
          console.log('✅ New session ID generated and stored:', existingSessionId);
        } else {
          console.log('✅ Existing session ID found:', existingSessionId);
        }
        
        return existingSessionId;
      } catch (error) {
        console.error('❌ Error managing session ID:', error);
        // Fallback: generate session ID without localStorage
        return generateUUIDv4();
      }
    };

    const currentSessionId = getOrCreateSessionId();
    setSessionId(currentSessionId);
  }, []); // Run only once on component mount

  // Verificar se o formulário já foi completado (apenas na sessão atual)
  useEffect(() => {
    const formCompleted = sessionStorage.getItem('nukard_form_completed');
    if (formCompleted === 'true') {
      setIsFormCompleted(true);
    }
  }, []);

  // Persist session ID on every render (if it gets lost somehow)
  useEffect(() => {
    if (sessionId) {
      try {
        const storedSessionId = localStorage.getItem('nukard_session');
        if (storedSessionId !== sessionId) {
          localStorage.setItem('nukard_session', sessionId);
          console.log('✅ Session ID persisted:', sessionId);
        }
      } catch (error) {
        console.error('❌ Error persisting session ID:', error);
      }
    }
  }, [sessionId]); // Run whenever sessionId changes

  // Dados das etapas do formulário
  const steps = useMemo(() => [
    {
      id: 'step1',
      title: 'Vamos nos conhecer melhor',
      message: 'Conte-nos um pouco sobre você e seu projeto',
      type: 'input',
      fields: [
        {
          name: 'fullName',
          label: 'Seu nome completo',
          placeholder: 'João Silva Santos',
          type: 'text',
          required: true,
          ariaLabel: 'Digite seu nome completo'
        },
        {
          name: 'companyName',
          label: 'Nome da empresa/projeto',
          placeholder: 'Minha Empresa LTDA',
          type: 'text',
          required: true,
          ariaLabel: 'Digite o nome da sua empresa ou projeto'
        },
        {
          name: 'businessArea',
          label: 'Área de atuação (o que vocês fazem?)',
          placeholder: 'Prestação de serviços, comércio, consultoria, alimentação, estética, saúde',
          type: 'text',
          required: true,
          ariaLabel: 'Descreva a área de atuação da sua empresa'
        }
      ]
    },
    {
      id: 'step2',
      title: 'Sobre o seu projeto',
      message: 'Vamos entender melhor o que você precisa',
      type: 'multi_cards',
      fields: [
        {
          name: 'projectTypes',
          label: 'Tipo do projeto desejado (pode escolher mais de uma opção)',
          type: 'multi_cards',
          required: true,
          ariaLabel: 'Selecione o tipo de projeto desejado',
          options: [
            { value: 'website', label: 'Site Profissional', icon: '🌐', desc: 'Site institucional moderno e responsivo' },
            { value: 'app', label: 'Aplicativo de celular', icon: '📱', desc: 'App nativo para iOS e Android' }
          ]
        },
        {
          name: 'appDetails',
          label: 'Qual é a sua ideia?',
          placeholder: 'Descreva tudo o que tiver em mente para o seu projeto',
          type: 'textarea',
          required: false,
          ariaLabel: 'Descreva sua ideia de projeto'
        }
      ]
    },
    {
      id: 'stepAI',
      title: 'Pergunta personalizada',
      message: 'Com base no que você me contou, tenho uma pergunta específica para o seu projeto',
      type: 'ai_question',
      fields: [
        {
          name: 'userResponse',
          label: 'aiQuestion', // Será substituído pela pergunta da IA
          placeholder: 'Aguarde enquanto preparamos uma pergunta personalizada para você...',
          type: 'textarea',
          required: true,
          ariaLabel: 'Responda à pergunta personalizada da IA'
        }
      ]
    },
    {
      id: 'step3',
      title: 'Identidade visual',
      message: 'Nos conte sobre sua marca e preferências visuais',
      type: 'input',
      fields: [
        {
          name: 'brandStatus',
          label: 'Você possui marca ou identidade visual definida?',
          placeholder: 'Sim, já tenho tudo pronto ou Não, preciso de ajuda para criar',
          type: 'text',
          required: true,
          ariaLabel: 'Informe se você já possui identidade visual definida'
        },
        {
          name: 'stylePreference',
          label: 'Preferência por cores ou estilo específico? (Opcional)',
          placeholder: 'Pode deixar em branco se não tiver preferência',
          type: 'text',
          required: false,
          ariaLabel: 'Descreva suas preferências de cores e estilo'
        },
        {
          name: 'designReference',
          label: 'Existe algum site ou aplicativo como referência/inspiração?',
          placeholder: 'Digite links ou nomes aqui',
          type: 'text',
          required: false,
          ariaLabel: 'Cite referências ou inspirações de design'
      }
      ]
    },
    {
      id: 'step4',
      title: 'Prazo de entrega',
      message: 'Suas preferências de prazo para entrega do projeto',
      type: 'mixed',
      fields: [
        {
          name: 'timeline',
          label: 'Qual o prazo ideal para entrega do projeto?',
          type: 'cards',
          required: true,
          ariaLabel: 'Selecione o prazo desejado para o projeto',
          options: [
            {
              value: 'urgent',
              label: 'Urgente',
              icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" fill="white"/>
                </svg>
              ),
              desc: 'Até 7 dias'
            },
            {
              value: 'normal',
              label: 'Normal',
              icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2"/>
                  <path d="M12 6V12L16 14" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ),
              desc: '2-3 semanas'
            },
            {
              value: 'flexible',
              label: 'Flexível',
              icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="5" width="18" height="16" rx="2" stroke="white" strokeWidth="2"/>
                  <path d="M16 3V7" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M8 3V7" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M3 11H21" stroke="white" strokeWidth="2"/>
                </svg>
              ),
              desc: '5-6 semanas'
            }
          ]
        },
      ]
    },
    {
      id: 'step5',
      title: 'Informações de contato',
      message: 'Para finalizarmos, precisamos de seus dados de contato',
      type: 'input',
      fields: [
        {
          name: 'phone',
          label: 'Telefone para contato',
          placeholder: '(11) 99999-9999',
          type: 'tel',
          required: true,
          ariaLabel: 'Digite seu telefone para contato'
        },
        {
          name: 'email',
          label: 'Email',
          placeholder: 'joao@exemplo.com',
          type: 'email',
          required: true,
          ariaLabel: 'Digite seu email'
        },
        {
          name: 'additionalInfo',
          label: 'Gostaria de adicionar mais alguma coisa que não foi discutido?',
          placeholder: 'Fique à vontade para mencionar qualquer coisa que não foi abordada.',
          type: 'text',
          required: false,
          ariaLabel: 'Adicione informações complementares se desejar'
        }
      ]
    },
    {
      id: 'conclusion',
      title: 'Perfeito! Tudo pronto!',
      message: emailSent 
        ? 'Recebi todas as informações e um email de confirmação foi enviado para você! Em breve nossa equipe entrará em contato com uma proposta personalizada para seu projeto.'
        : 'Recebi todas as informações. Em breve nossa equipe entrará em contato com uma proposta personalizada para seu projeto.',
      type: 'conclusion',
      fields: []
    }
  ], [emailSent]);

  // Filtrar steps baseado em se temos AI step ou não
  const filteredSteps = useMemo(() => {
    if (hasAIStep) {
      return steps; // Inclui todos os steps, incluindo stepAI
    } else {
      return steps.filter(step => step.id !== 'stepAI'); // Remove stepAI
    }
  }, [hasAIStep, steps]);

  const currentStepData = filteredSteps[currentStep];

  
  // Calcular progresso incluindo "Vamos nos conhecer melhor" mas excluindo conclusão
  const getProgressInfo = () => {
    const mainSteps = filteredSteps.length - 1; // Excluir apenas a conclusão
    
    if (currentStep === filteredSteps.length - 1) {
      return { shouldShow: false, current: mainSteps, total: mainSteps, percentage: 100 };
    }
    
    const currentMainStep = currentStep + 1; // +1 para mostrar "1 de 5" ao invés de "0 de 5"
    const percentage = Math.round((currentMainStep / mainSteps) * 100);
    
    return { 
      shouldShow: true, 
      current: currentMainStep, 
      total: mainSteps, 
      percentage 
    };
  };
  
  const progressInfo = getProgressInfo();

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isModalOpen) return;
      
      if (e.key === 'Escape') {
        closeModal();
      }
      
      if (e.key === 'Tab') {
        // Handle tab trapping within modal
        const focusableElements = modalRef.current?.querySelectorAll(
          'button, input, textarea, [tabindex]:not([tabindex="-1"])'
        );
        
        if (focusableElements && focusableElements.length > 0) {
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];
          
          if (e.shiftKey) {
            if (document.activeElement === firstElement) {
              lastElement.focus();
              e.preventDefault();
            }
          } else {
            if (document.activeElement === lastElement) {
              firstElement.focus();
              e.preventDefault();
            }
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);

  // Efeito de digitação otimizado com respeito a preferências de acessibilidade
  useEffect(() => {
    if (currentStepData.message && isModalOpen && !isModalLoading && !isAnimating) {
      setIsTyping(false);
      setDisplayedText(currentStepData.message);
      setShowFields(true);
      
      // Verificação extra para step AI
      if (currentStepData.type === 'ai_question') {
        setTimeout(() => setShowFields(true), 100);
      }
      
      setTimeout(() => {
        if (firstFocusableRef.current) {
          firstFocusableRef.current.focus();
        }
      }, 100);
    }
  }, [currentStep, currentStepData.message, currentStepData.type, isModalOpen, isModalLoading, prefersReducedMotion, isAnimating]);

  // Gerenciar dados do formulário
  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value || ''
    }));
  };

  // Gerenciar campos de telefone formatado
  const handlePhoneChange = (part, value) => {
    // Permitir apenas números
    const numericValue = value.replace(/\D/g, '');
    
    if (part === 'area') {
      const limitedValue = numericValue.slice(0, 2);
      setPhoneArea(limitedValue);
    } else if (part === 'first') {
      const limitedValue = numericValue.slice(0, 5);
      setPhoneFirst(limitedValue);
    } else if (part === 'last') {
      const limitedValue = numericValue.slice(0, 4);
      setPhoneLast(limitedValue);
    }
    
    // Atualizar formData com telefone completo
    const area = part === 'area' ? numericValue.slice(0, 2) : phoneArea;
    const first = part === 'first' ? numericValue.slice(0, 5) : phoneFirst;
    const last = part === 'last' ? numericValue.slice(0, 4) : phoneLast;
    
    const fullPhone = area || first || last ? `(${area}) ${first}${last ? '-' + last : ''}` : '';
    
    setFormData(prev => ({
      ...prev,
      phone: fullPhone
    }));
  };

  const handleCardSelect = (value) => {
    setFormData(prev => ({
      ...prev,
      [currentStepData.id]: value
    }));
  };

  // Handle multi-card selection
  const handleMultiCardSelect = (fieldName, value) => {
    setFormData(prev => {
      const currentValues = prev[fieldName] || [];
      const isSelected = currentValues.includes(value);
      
      if (isSelected) {
        // Remove the value if already selected
        return {
          ...prev,
          [fieldName]: currentValues.filter(v => v !== value)
        };
      } else {
        // Add the value if not selected
        return {
          ...prev,
          [fieldName]: [...currentValues, value]
        };
      }
    });
  };

  // Navegação com animações suaves
  const handleNext = async () => {
    if (currentStep < filteredSteps.length - 1 && !isAnimating && !isFading) {
      
      // STEP 1 → STEP 2: Salvar informações básicas
      if (currentStep === 0) {
        try {
          console.log('💾 Saving Step 1 data...');
          
          // Sincronizar session_id no localStorage para saveDraft
          localStorage.setItem('session_id', sessionId);
          
          const step1Fields = {
            full_name: formData.fullName,
            company_name: formData.companyName,
            business_area: formData.businessArea
          };

          const draftId = await QuestionnaireService.saveDraft({
            ...step1Fields,
            step: 2
          });

          console.log('✅ Step 1 data saved successfully:', draftId);
        } catch (error) {
          console.error('❌ Error saving Step 1 data:', error);
          // Continue to next step even if save fails
        }
      }

      // STEP 2 → STEP AI: Salvar detalhes do projeto e disparar AI
      if (currentStep === 1) {
        try {
          console.log('💾 Saving Step 2 data and triggering AI...');
          
          // Sincronizar session_id no localStorage para saveDraft
          localStorage.setItem('session_id', sessionId);
          
          const step2Fields = {
            project_types: formData.projectTypes,
            app_details: formData.appDetails
          };

          const draftId = await QuestionnaireService.saveDraft({
            ...step2Fields,
            step: 3
          });

          console.log('✅ Step 2 data saved successfully:', draftId);
          
          // Disparar AI workflow e não avançar step ainda
          setIsWaitingForAI(true);
          await triggerAIWorkflow();
          
          // triggerAIWorkflow() vai avançar o step quando terminar
          return; // Não avança step aqui!
          
        } catch (error) {
          console.error('❌ Error saving Step 2 data or triggering AI:', error);
          // Continue to next step even if save fails
        }
      }

      // STEP AI → STEP 3: Salvar resposta da IA
      if (currentStep === 2 && hasAIStep) {
        try {
          console.log('💾 Saving AI response...');
          
          // Sincronizar session_id no localStorage para saveDraft
          localStorage.setItem('session_id', sessionId);
          
          await QuestionnaireService.saveUserResponse(sessionId, formData.userResponse);
          
          console.log('✅ AI response saved successfully');
        } catch (error) {
          console.error('❌ Error saving AI response:', error);
          // Continue to next step even if save fails
        }
      }

      // STEP 3 → STEP 4: Salvar identidade visual
      if ((currentStep === 2 && !hasAIStep) || (currentStep === 3 && hasAIStep)) {
        try {
          console.log('💾 Saving Step 3 data...');
          
          // Sincronizar session_id no localStorage para saveDraft
          localStorage.setItem('session_id', sessionId);
          
          const step3Fields = {
            brand_status: formData.brandStatus,
            style_preference: formData.stylePreference,
            design_reference: formData.designReference
          };

          const draftId = await QuestionnaireService.saveDraft({
            ...step3Fields,
            step: 4
          });

          console.log('✅ Step 3 data saved successfully:', draftId);
        } catch (error) {
          console.error('❌ Error saving Step 3 data:', error);
          // Continue to next step even if save fails
        }
      }

      // STEP 4 → STEP 5: Salvar timeline
      if ((currentStep === 3 && !hasAIStep) || (currentStep === 4 && hasAIStep)) {
        try {
          console.log('💾 Saving Step 4 data...');
          
          // Sincronizar session_id no localStorage para saveDraft
          localStorage.setItem('session_id', sessionId);
          
          const step4Fields = {
            timeline: formData.timeline
          };

          const draftId = await QuestionnaireService.saveDraft({
            ...step4Fields,
            step: 5
          });

          console.log('✅ Step 4 data saved successfully:', draftId);
        } catch (error) {
          console.error('❌ Error saving Step 4 data:', error);
          // Continue to next step even if save fails
        }
      }

      // STEP 5 → CONCLUSÃO: Salvar dados de contato e FINALIZAR
      if ((currentStep === 4 && !hasAIStep) || (currentStep === 5 && hasAIStep)) {
        // Ativar estado de loading
        setIsSubmitting(true);
        
        try {
          console.log('💾 Finalizing form with Step 5 data...');
          console.log('📋 Current form data:', formData);
          console.log('🔑 Session ID:', sessionId);
          
          // Verificar se sessionId existe
          if (!sessionId) {
            throw new Error('Session ID não encontrado');
          }
          
          // Verificar se campos obrigatórios estão preenchidos
          if (!formData.phone || !formData.email) {
            throw new Error('Campos obrigatórios não preenchidos');
          }
          
          // Coletar dados da etapa 5
          const step5Fields = {
            phone: formData.phone,
            email: formData.email,
            additional_info: formData.additionalInfo || ''
          };

          console.log('📞 Step 5 fields:', step5Fields);

          // Finalizar: atualizar registro existente com dados completos e status "completed"
          console.log('💾 Updating existing record with final data and status completed...');
          localStorage.setItem('session_id', sessionId);
          
          // Preparar todos os dados finais para salvar
          const finalFormData = {
            // Step 1
            full_name: formData.fullName,
            company_name: formData.companyName,
            business_area: formData.businessArea,
            
            // Step 2
            project_types: formData.projectTypes,
            app_details: formData.appDetails,
            
            // Step 3
            brand_status: formData.brandStatus,
            style_preference: formData.stylePreference,
            design_reference: formData.designReference,
            
            // Step 4
            timeline: formData.timeline,
            
            // Step 5
            phone: formData.phone,
            email: formData.email,
            additional_info: formData.additionalInfo || '',
            
            // Finalização
            status: 'completed',
            step: 6, // Indica que todas as etapas foram completadas
            raw_form_data: formData // Backup completo dos dados
          };
          
          console.log('📦 Final form data to save:', finalFormData);
          
          const finalRecordId = await QuestionnaireService.saveDraft(finalFormData);
          
          console.log('✅ Form completed successfully!');
          console.log('📄 Updated record ID:', finalRecordId);
          console.log('🔑 Session ID:', sessionId);
          
          // Armazenar session ID para referência futura
          sessionStorage.setItem('lastQuestionnaireSession', sessionId);
          
          // Mostrar mensagem de sucesso
          console.log('🎉 Formulário enviado com sucesso!');
          
          // Disparar webhook n8n para envio de email
          console.log('📧 Triggering n8n email webhook...');
          await triggerEmailNotification(finalFormData);
          
          // Marcar formulário como completado para bloquear reinicialização (apenas na sessão atual)
          sessionStorage.setItem('nukard_form_completed', 'true');
          sessionStorage.setItem('nukard_completion_timestamp', new Date().toISOString());
          setIsFormCompleted(true);
          
          // Não fechar automaticamente - usuário deve fechar manualmente
          
        } catch (error) {
          console.error('❌ Error finalizing form:', error);
          console.error('📊 Error details:', {
            message: error.message,
            sessionId: sessionId,
            formData: formData,
            step: currentStep
          });
          
          alert(`Ops! Houve um problema ao enviar seu formulário: ${error.message}\n\nTente novamente ou verifique se todos os campos obrigatórios estão preenchidos.`);
          // Não avança para conclusão se houver erro
          return;
        } finally {
          // Desativar estado de loading sempre
          setIsSubmitting(false);
        }
      }

      setIsFading(true);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsFading(false);
      }, 280); // duração do fade-out
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0 && !isAnimating && !isFading) {
      setIsFading(true);
      setTimeout(() => {
        setCurrentStep(currentStep - 1);
        setIsFading(false);
      }, 280); // duração do fade-out
    }
  };

  const canProceed = () => {
    if (currentStepData.type === 'intro' || currentStepData.type === 'conclusion') {
      return true;
    }
    
    if (currentStepData.type === 'cards') {
      return formData[currentStepData.id];
    }

    if (currentStepData.type === 'multi_cards') {
      return currentStepData.fields.every(field => {
        if (!field.required) return true;
        const value = formData[field.name];
        return value && Array.isArray(value) && value.length > 0;
      });
    }

    if (currentStepData.type === 'toggle_cards') {
      return true;
    }

    if (currentStepData.type === 'slider_cards') {
      return currentStepData.questions.every(question => 
        formData[question.name]
      );
    }

    if (currentStepData.type === 'mixed') {
      return currentStepData.fields.every(field => {
        if (!field.required) return true;
        const value = formData[field.name];
        return value && typeof value === 'string' && value.trim().length > 0;
      });
    }

    if (currentStepData.type === 'ai_question') {
      // Para o step AI, verifica se não estamos aguardando e se a pergunta foi respondida
      if (isWaitingForAI || !aiQuestion) {
        return false;
      }
      return formData.userResponse && formData.userResponse.trim().length > 0;
    }

    if (currentStepData.fields) {
      return currentStepData.fields.every(field => {
        if (!field.required) return true;
        
        // Validação especial para campo de telefone
        if (field.name === 'phone') {
          return phoneArea.length === 2 && phoneFirst.length === 5 && phoneLast.length === 4;
        }
        
        const value = formData[field.name];
        return value && typeof value === 'string' && value.trim().length > 0;
      });
    }
    
    return true;
  };

  // Abrir modal com animação
  const openModal = () => {
    // Prevent multiple clicks
    if (isModalLoading || isModalOpen) return;
    
    // Bloquear se formulário já foi completado
    if (isFormCompleted) {
      console.log('🚫 Formulário já foi completado. Bloqueando reinicialização.');
      return;
    }
    
    setIsModalLoading(true);
    
    // GERAR NOVO SESSION ID a cada formulário
    const newSessionId = generateUUIDv4();
    setSessionId(newSessionId);
    localStorage.setItem('nukard_session', newSessionId);
    console.log('🆕 New session started:', newSessionId);
    
    // Reset state first, then open modal
    setCurrentStep(0);
    setFormData({});
    setDisplayedText('');
    setIsTyping(false);
    setShowFields(false);
    setIsAnimating(false);
    setPhoneArea('');
    setPhoneFirst('');
    setPhoneLast('');
    
    // Then open the modal
    setTimeout(() => {
      setIsModalOpen(true);
      document.body.style.overflow = 'hidden';
      setIsModalLoading(false);
    }, 50); // Small delay to ensure state is properly reset
  };

  // Fechar modal
  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'auto';
    
    // Limpar localStorage para evitar conflitos
    localStorage.removeItem('session_id');
    localStorage.removeItem('nukard_record_id');
    console.log('🧹 LocalStorage cleaned');
    
    // Reset all state properly
    setTimeout(() => {
      setCurrentStep(0);
      setFormData({});
      setDisplayedText('');
      setIsTyping(false);
      setShowFields(false);
      setIsAnimating(false);
      setIsModalLoading(false);
      setSessionId(null);
      setPhoneArea('');
      setPhoneFirst('');
      setPhoneLast('');
      setEmailSent(false);
      setIsSubmitting(false);
    }, 400); // Wait for modal close animation
  };

  // Função para disparar o webhook de email do n8n
  const triggerEmailNotification = async (formData) => {
    try {
      console.log('📧 Sending form data to n8n email webhook...');
      
      // Preparar dados para enviar para N8N (formato que o webhook espera)
      const emailData = {
        session_id: sessionId,
        name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        company_name: formData.company_name,
        business_area: formData.business_area,
        project_types: formData.project_types,
        app_details: formData.app_details,
        brand_status: formData.brand_status,
        style_preference: formData.style_preference,
        design_reference: formData.design_reference,
        timeline: formData.timeline,
        additional_info: formData.additional_info,
        submitted_at: new Date().toISOString()
      };
      
      console.log('📤 Sending email data to n8n:', emailData);
      
      // Chamar webhook do N8N para email
      const response = await fetch('https://nukard.app.n8n.cloud/webhook/emailautomatico', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData)
      });
      
      if (!response.ok) {
        throw new Error(`N8N email webhook failed: ${response.status} - ${response.statusText}`);
      }
      
      const result = await response.text(); // N8N pode retornar texto simples
      console.log('✅ N8N email webhook response:', result);
      
      // Mostrar feedback visual para o usuário
      console.log('📧 Email de confirmação enviado com sucesso!');
      setEmailSent(true);
      
    } catch (error) {
      console.error('❌ Error triggering email notification:', error);
      // Não interromper o fluxo se o email falhar
      console.warn('⚠️ Email notification failed, but form submission was successful');
    }
  };

  // Função para disparar o workflow de IA
  const triggerAIWorkflow = async () => {
    try {
      console.log('🤖 Triggering AI workflow...');
      
      // Preparar dados para enviar para N8N
      const webhookData = {
        session_id: sessionId,
        step1: {
          fullName: formData.fullName,
          companyName: formData.companyName,
          businessArea: formData.businessArea
        },
        step2: {
          projectTypes: formData.projectTypes,
          appDetails: formData.appDetails
        }
      };
      
      console.log('📤 Sending data to N8N:', webhookData);
      
      // Chamar webhook do N8N
      const response = await fetch('https://nukard.app.n8n.cloud/webhook/ai-followup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });
      
      if (!response.ok) {
        throw new Error(`N8N webhook failed: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('✅ N8N webhook response:', result);
      
      // Capturar a pergunta diretamente da resposta do N8N
      // Suporta diferentes formatos: {ai_question: "..."} ou {question: "..."} ou string direta
      let aiQuestionText = null;
      
      if (result && typeof result === 'object') {
        aiQuestionText = result.ai_question || result.question || result.aiQuestion;
      } else if (typeof result === 'string') {
        aiQuestionText = result;
      }
      
      if (aiQuestionText && aiQuestionText.trim()) {
        console.log('🤖 AI question received from N8N:', aiQuestionText);
        setAiQuestion(aiQuestionText.trim());
        setHasAIStep(true);
        
        // Salvar a pergunta no Supabase também (para backup)
        try {
          await QuestionnaireService.updateAIQuestion(sessionId, aiQuestionText);
          console.log('✅ AI question saved to Supabase as backup');
    } catch (error) {
          console.warn('⚠️ Failed to save AI question to Supabase:', error);
        }
        
        // Avançar para o step AI
        setTimeout(() => {
          setIsFading(true);
          setTimeout(() => {
            setCurrentStep(prev => prev + 1);
            setIsFading(false);
          }, 280);
        }, 500);
        
      } else {
        console.log('⚠️ No valid AI question in N8N response, skipping AI step');
        setHasAIStep(false);
        
        // Avançar direto para step 3 (pular AI step)
        setTimeout(() => {
          setIsFading(true);
          setTimeout(() => {
            setCurrentStep(prev => prev + 1);
            setIsFading(false);
          }, 280);
        }, 500);
      }
      
      setIsWaitingForAI(false);
      
    } catch (error) {
      console.error('❌ Error triggering AI workflow:', error);
      setIsWaitingForAI(false);
      setHasAIStep(false);
      
      // Em caso de erro, avançar direto para step 3 (pular AI step)
      setTimeout(() => {
        setIsFading(true);
        setTimeout(() => {
          setCurrentStep(prev => prev + 1);
          setIsFading(false);
        }, 280);
      }, 500);
    }
  };

  // Função handleFinishForm removida - agora o formulário é finalizado no handleNext da etapa 5

  return (
    <>
      {/* Seção inicial com botão para abrir o formulário */}
      <section id="form-section" className="form-section-intro">
        <div className="intro-container">
          <div className="intro-content">
            {/* Robot Avatar */}
            <div className="nk-avatar intro-avatar">
              <img 
                src="/images/robo.png" 
                alt="NK Robot" 
                className="avatar-robot intro-robot"
              />
            </div>
            
            <h2 className="intro-title">
              Pronto para criar sua
              <span className="gradient-text"> solução digital</span><span className="gradient-text">?</span>
            </h2>
            <p className="intro-description">
              Faça nosso formulário inteligente e comece a desenvolver sua ideia com a Nukard.
            </p>
            <button 
              className={`start-button ${isFormCompleted ? 'completed' : ''}`}
              onClick={openModal}
              disabled={isModalLoading || isFormCompleted}
            >
              <span className="button-text">
                {isFormCompleted 
                  ? 'Já recebemos suas informações!' 
                  : isModalLoading 
                    ? 'Carregando...' 
                    : 'Começar Agora'
                }
              </span>
              <div className="button-glow"></div>
            </button>
          </div>
        </div>
      </section>

      {/* Modal do formulário */}
      {isModalOpen && (
        <div 
          className={`form-modal ${isModalOpen ? 'modal-open' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
          ref={modalRef}
        >
          <div className="modal-backdrop" onClick={closeModal} aria-hidden="true"></div>
          <div className="modal-content">
            <NeuralPoints />
            
            {/* Botão de fechar */}
            <button 
              className="close-button" 
              onClick={closeModal}
              aria-label="Fechar formulário"
              title="Pressione Escape para fechar"
            >
              <span aria-hidden="true">×</span>
            </button>
            
            {/* Barra de progresso */}
            {progressInfo.shouldShow && (
              <div className="progress-container" role="progressbar" aria-valuenow={progressInfo.percentage} aria-valuemin="0" aria-valuemax="100">
                <div className="progress-header">
                  <span className="progress-text" aria-label={`Etapa ${progressInfo.current} de ${progressInfo.total}`}>
                    {progressInfo.current} de {progressInfo.total}
                  </span>
                  <span className="progress-text" aria-label={`${progressInfo.percentage}% concluído`}>
                    {progressInfo.percentage}%
                  </span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${progressInfo.percentage}%` }}
                    aria-hidden="true"
                  />
                  <div className="progress-glow" aria-hidden="true" />
                </div>
              </div>
            )}

            {/* Container principal */}
            <div className={`form-container${currentStepData.type === 'conclusion' ? ' conclusion-no-scroll' : ''}`}>
              <div className={`form-content${currentStepData.type === 'intro' || currentStepData.type === 'conclusion' ? ' welcome-layout' : ''}${currentStepData.type === 'conclusion' ? ' conclusion-padding conclusion-center' : ''} fade-step${isFading ? ' fade-out' : ' fade-in'}`}>
                {/* Avatar e conversa */}
                <div className={`conversation-area ${currentStepData.type === 'intro' || currentStepData.type === 'conclusion' ? 'welcome-conversation' : ''}`}>
                  <div className={`nk-avatar ${currentStepData.type === 'intro' || currentStepData.type === 'conclusion' ? 'welcome-avatar' : ''}`}>
                    <img 
                      src="/images/robo.png" 
                      alt="NK Robot" 
                      className="avatar-robot modal-robot"
                    />
                  </div>
                  
                  <div className={`message-bubble ${currentStepData.type === 'intro' || currentStepData.type === 'conclusion' ? 'welcome-bubble' : ''}`}>
                    <h2 
                      id="modal-title"
                      className={`message-title ${currentStepData.type === 'intro' || currentStepData.type === 'conclusion' ? 'welcome-title' : ''}`}
                    >
                      {currentStepData.title}
                    </h2>
                    <p 
                      id="modal-description"
                      className={`message-text ${currentStepData.type === 'intro' || currentStepData.type === 'conclusion' ? 'welcome-text' : ''}`}
                      aria-live="polite"
                    >
                      {displayedText}
                      {isTyping && <span className="cursor-blink" aria-hidden="true">|</span>}
                    </p>
                  </div>
                </div>

                {/* Campos do formulário */}
                <div className={`form-fields ${showFields ? 'fields-visible' : ''}`}>
                  {(currentStepData.type === 'intro' || currentStepData.type === 'conclusion') && (
                    <div className="intro-content-area">
                      {/* This is for intro and conclusion steps that don't have form fields */}
                    </div>
                  )}

                  {currentStepData.type === 'input' && currentStepData.fields.map((field, index) => (
                    <div key={field.name} className="field-group" style={{ animationDelay: `${index * 0.1}s` }}>
                      <label className="field-label" htmlFor={field.name}>
                        {field.label}
                        {field.required && <span className="required-indicator" aria-label="obrigatório">*</span>}
                      </label>
                      {field.name === 'phone' ? (
                        <div className="phone-input-group">
                          <div className="phone-part">
                            <span className="phone-prefix">(</span>
                            <input
                              type="tel"
                              className="form-input phone-area"
                              placeholder="41"
                              value={phoneArea}
                              onChange={(e) => handlePhoneChange('area', e.target.value)}
                              maxLength={2}
                              aria-label="DDD - código de área"
                              ref={index === 0 ? firstFocusableRef : null}
                            />
                            <span className="phone-suffix">)</span>
                          </div>
                          <div className="phone-part">
                            <input
                              type="tel"
                              className="form-input phone-first"
                              placeholder="99999"
                              value={phoneFirst}
                              onChange={(e) => handlePhoneChange('first', e.target.value)}
                              maxLength={5}
                              aria-label="Primeiros 5 dígitos do telefone"
                            />
                            <span className="phone-separator">-</span>
                          </div>
                          <div className="phone-part">
                            <input
                              type="tel"
                              className="form-input phone-last"
                              placeholder="9999"
                              value={phoneLast}
                              onChange={(e) => handlePhoneChange('last', e.target.value)}
                              maxLength={4}
                              aria-label="Últimos 4 dígitos do telefone"
                            />
                          </div>
                        </div>
                      ) : field.type === 'textarea' ? (
                        <textarea
                          id={field.name}
                          className="form-input form-textarea"
                          placeholder={field.placeholder || ''}
                          value={formData[field.name] || ''}
                          onChange={(e) => handleInputChange(field.name, e.target.value)}
                          aria-label={field.ariaLabel}
                          aria-required={field.required}
                          rows={3}
                          ref={index === 0 ? firstFocusableRef : null}
                        />
                      ) : (
                        <input
                          id={field.name}
                          type={field.type || 'text'}
                          className="form-input"
                          placeholder={field.placeholder || ''}
                          value={formData[field.name] || ''}
                          onChange={(e) => handleInputChange(field.name, e.target.value)}
                          aria-label={field.ariaLabel}
                          aria-required={field.required}
                          ref={index === 0 ? firstFocusableRef : null}
                        />
                      )}
                    </div>
                  ))}

                  {currentStepData.type === 'cards' && (
                    <div className="cards-container">
                      {currentStepData.options.map((option, index) => (
                        <div
                          key={option.value}
                          className={`option-card ${formData[currentStepData.id] === option.value ? 'selected' : ''}`}
                          onClick={() => handleCardSelect(option.value)}
                          style={{ animationDelay: `${index * 0.1}s` }}
                        >
                          <div className="card-icon">{option.icon}</div>
                          <div className="card-content">
                            <h3 className="card-title">{option.label}</h3>
                            <p className="card-desc">{option.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {currentStepData.type === 'mixed' && currentStepData.fields.map((field, index) => (
                    <div key={field.name} className="field-group" style={{ animationDelay: `${index * 0.1}s` }}>
                      {field.type === 'cards' ? (
                        <>
                          <label className="field-label">{field.label}</label>
                          <div className={`cards-container ${field.name === 'timeline' ? 'timeline-cards' : ''}`}>
                            {field.options.map((option, optionIndex) => (
                              <div
                                key={option.value}
                                className={`option-card ${formData[field.name] === option.value ? 'selected' : ''}`}
                                onClick={() => handleInputChange(field.name, option.value)}
                                style={{ animationDelay: `${optionIndex * 0.1}s` }}
                              >
                                <div className="card-icon">{option.icon}</div>
                                <div className="card-content">
                                  <h3 className="card-title">{option.label}</h3>
                                  <p className="card-desc">{option.desc}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <>
                          <label className="field-label" htmlFor={field.name}>
                            {field.label}
                            {field.required && <span className="required-indicator" aria-label="obrigatório">*</span>}
                          </label>
                          {field.type === 'textarea' ? (
                            <textarea
                              id={field.name}
                              className="form-input form-textarea"
                              placeholder={field.placeholder || ''}
                              value={formData[field.name] || ''}
                              onChange={(e) => handleInputChange(field.name, e.target.value)}
                              aria-label={field.ariaLabel}
                              aria-required={field.required}
                              rows={3}
                            />
                          ) : (
                            <input
                              id={field.name}
                              type={field.type || 'text'}
                              className="form-input"
                              placeholder={field.placeholder || ''}
                              value={formData[field.name] || ''}
                              onChange={(e) => handleInputChange(field.name, e.target.value)}
                              aria-label={field.ariaLabel}
                              aria-required={field.required}
                            />
                          )}
                        </>
                      )}
                    </div>
                  ))}

                  {currentStepData.type === 'multi_cards' && currentStepData.fields.map((field, index) => (
                    <div key={field.name} className="field-group" style={{ animationDelay: `${index * 0.1}s` }}>
                      {field.type === 'multi_cards' ? (
                        <>
                          <fieldset className="field-fieldset">
                            <legend className="field-label">
                              {field.label}
                              {field.required && <span className="required-indicator" aria-label="obrigatório">*</span>}
                            </legend>
                            <div className="cards-container" role="group" aria-label={field.ariaLabel}>
                              {field.options.map((option, optionIndex) => {
                                const isSelected = (formData[field.name] || []).includes(option.value);
                                return (
                                  <button
                                    key={option.value}
                                    type="button"
                                    className={`option-card ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleMultiCardSelect(field.name, option.value)}
                                    style={{ animationDelay: `${optionIndex * 0.1}s` }}
                                    aria-pressed={isSelected}
                                    aria-describedby={`${option.value}-desc`}
                                    ref={index === 0 && optionIndex === 0 ? firstFocusableRef : null}
                                  >
                                    <div className="card-icon" aria-hidden="true">{option.icon}</div>
                                    <div className="card-content">
                                      <h3 className="card-title">{option.label}</h3>
                                      <p className="card-desc" id={`${option.value}-desc`}>{option.desc}</p>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </fieldset>
                        </>
                      ) : (
                        <>
                          <label className="field-label">{field.label}</label>
                          {field.type === 'textarea' ? (
                            <textarea
                              className="form-input form-textarea"
                              placeholder={field.placeholder || ''}
                              value={formData[field.name] || ''}
                              onChange={(e) => handleInputChange(field.name, e.target.value)}
                              rows={3}
                            />
                          ) : (
                            <input
                              type={field.type || 'text'}
                              className="form-input"
                              placeholder={field.placeholder || ''}
                              value={formData[field.name] || ''}
                              onChange={(e) => handleInputChange(field.name, e.target.value)}
                            />
                          )}
                        </>
                      )}
                    </div>
                  ))}

                  {currentStepData.type === 'slider_cards' && (
                    <div className="slider-cards-container">
                      {currentStepData.questions.map((question, questionIndex) => (
                        <fieldset key={question.name} className="slider-question" style={{ animationDelay: `${questionIndex * 0.2}s` }}>
                          <legend className="question-title">
                            {question.label}
                            <span className="required-indicator" aria-label="obrigatório">*</span>
                          </legend>
                          <div className="slider-options" role="radiogroup" aria-label={question.ariaLabel}>
                            {question.options.map((option, optionIndex) => (
                              <button
                                key={option.value}
                                type="button"
                                role="radio"
                                aria-checked={formData[question.name] === option.value}
                                className={`slider-option ${formData[question.name] === option.value ? 'selected' : ''}`}
                                onClick={() => handleInputChange(question.name, option.value)}
                                style={{ animationDelay: `${(questionIndex * 0.2) + (optionIndex * 0.1)}s` }}
                                aria-describedby={option.desc ? `${option.value}-desc` : undefined}
                                ref={questionIndex === 0 && optionIndex === 0 ? firstFocusableRef : null}
                              >
                                <div className="option-label">{option.label}</div>
                                {option.desc && <div className="option-desc" id={`${option.value}-desc`}>{option.desc}</div>}
                              </button>
                            ))}
                          </div>
                        </fieldset>
                      ))}
                    </div>
                  )}

                  {currentStepData.type === 'ai_question' && (
                    <div className="ai-question-container" style={{ display: 'block', visibility: 'visible', opacity: 1 }}>
                      {console.log('🎨 Renderizando AI step:', {
                        currentStepData: currentStepData.type,
                        isWaitingForAI,
                        aiQuestion,
                        hasAIStep,
                        currentStep,
                        filteredSteps: filteredSteps.length,
                        formData: formData.userResponse,
                        showFields: showFields
                      })}
                      
                      {isWaitingForAI ? (
                        <div className="ai-loading">
                          <div className="loading-spinner"></div>
                          <p className="loading-text">Analisando suas respostas e gerando uma pergunta personalizada...</p>
                        </div>
                      ) : aiQuestion ? (
                        <div className="field-group ai-question-field" style={{ display: 'block', visibility: 'visible', opacity: 1 }}>
                          <label className="field-label ai-question-label" style={{ display: 'block', visibility: 'visible', opacity: 1 }}>
                            {aiQuestion}
                            <span className="required-indicator" aria-label="obrigatório">*</span>
                          </label>
                          <textarea
                            className="form-input form-textarea ai-response-textarea"
                            placeholder="Compartilhe sua resposta aqui..."
                            value={formData.userResponse || ''}
                            onChange={(e) => handleInputChange('userResponse', e.target.value)}
                            aria-label="Responda à pergunta personalizada da IA"
                            aria-required={true}
                            rows={4}
                            ref={firstFocusableRef}
                            style={{ display: 'block', visibility: 'visible', opacity: 1 }}
                          />
                        </div>
                      ) : (
                        <div className="ai-error">
                          <p className="error-text">Não foi possível gerar uma pergunta personalizada. Vamos continuar com o próximo passo.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Botões de navegação - Ocultar na página de conclusão */}
                {currentStepData.type !== 'conclusion' && (
                <nav 
                    className={`navigation-buttons ${showFields ? 'buttons-visible' : ''} ${currentStepData.type === 'intro' ? 'welcome-buttons' : ''}`}
                  aria-label="Navegação do formulário"
                >
                  {currentStep > 0 && (
                    <button 
                      className="nav-button nav-back"
                      onClick={handlePrevious}
                      disabled={isAnimating}
                      aria-label="Voltar para etapa anterior"
                      ref={backButtonRef}
                    >
                      <span className="button-icon" aria-hidden="true">←</span>
                      Voltar
                    </button>
                  )}
                  
                  {/* Spacer to push next button to the right when back button is not present */}
                  {currentStep === 0 && <div className="nav-spacer" aria-hidden="true"></div>}
                  
                    <button 
                      className={`nav-button nav-next ${canProceed() && !isWaitingForAI && !isSubmitting ? 'enabled' : 'disabled'}${isSubmitting ? ' submitting' : ''}`}
                      onClick={handleNext}
                      disabled={!canProceed() || isAnimating || isWaitingForAI || isSubmitting}
                      aria-label={`${currentStep === filteredSteps.length - 2 ? 'Finalizar formulário' : 'Avançar para próxima etapa'}${!canProceed() ? ' (preencha os campos obrigatórios)' : ''}`}
                      ref={nextButtonRef}
                    >
                      {isWaitingForAI ? (
                        <>
                          <div className="loading-spinner-small"></div>
                          Preparando pergunta personalizada...
                        </>
                      ) : isSubmitting ? (
                        <>
                          <div className="loading-spinner-small"></div>
                          Enviando formulário...
                        </>
                      ) : (
                        <>
                          {currentStep === filteredSteps.length - 2 ? 'Finalizar' : 'Próximo'}
                          <span className="button-icon" aria-hidden="true">{currentStep === filteredSteps.length - 2 ? '✓' : '→'}</span>
                        </>
                      )}
                    </button>
                </nav>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FormSection; 